import { useRef, useState } from "react";
import { CANVAS_SHORTCUT_SECTIONS } from "../../config/canvasShortcuts";

// The Layout Canvas's keyboard and mouse shortcuts on one page, opened from the Help tile or with "?". The list is
// src/config/canvasShortcuts.js, pinned by a test to the canvas's real key handler.
//
// A docked, resizable flyout on the left of the drawing — not a modal — so an estimator can leave it open while they
// work. It has no backdrop and takes no keys: Esc keeps backing out of the active tool, and "?" (handled by the canvas)
// or the ✕ closes it. Width is a per-viewer preference.

const cap = { display: "inline-block", minWidth: 22, textAlign: "center", fontSize: 11, fontWeight: 600, padding: "1px 6px", border: "1px solid var(--cs-border)", borderBottomWidth: 2, borderRadius: 4, background: "var(--surface2, var(--surface))", color: "var(--text)", fontFamily: "ui-monospace, SFMono-Regular, Menlo, Consolas, monospace" };

export const GUIDE_WIDTH_KEY = "cs.takeoff.shortcutGuide.width";
export const GUIDE_MIN_W = 240;
export const GUIDE_MAX_W = 720;
const GUIDE_DEFAULT_W = 340;

export const clampGuideWidth = (w, available = Infinity) => {
  const n = Number(w);
  const base = Number.isFinite(n) ? n : GUIDE_DEFAULT_W;
  return Math.round(Math.max(GUIDE_MIN_W, Math.min(GUIDE_MAX_W, Number.isFinite(available) ? Math.min(base, Math.max(GUIDE_MIN_W, available)) : base)));
};

const readWidth = () => {
  try { return clampGuideWidth(localStorage.getItem(GUIDE_WIDTH_KEY) ?? GUIDE_DEFAULT_W); } catch { return GUIDE_DEFAULT_W; }
};

export default function ShortcutGuide({ onClose }) {
  const [width, setWidth] = useState(readWidth);
  const widthRef = useRef(width); widthRef.current = width;
  const panelRef = useRef(null);

  const startResize = (e) => {
    e.preventDefault();
    const startX = e.clientX, startW = widthRef.current;
    // Never wider than ~80% of the drawing area it sits over, so the canvas stays usable.
    const parentW = panelRef.current?.parentElement?.clientWidth || 0;
    const avail = parentW > 0 ? parentW * 0.8 : Infinity;
    const move = (ev) => setWidth(clampGuideWidth(startW + (ev.clientX - startX), avail));
    const up = () => {
      window.removeEventListener("pointermove", move);
      window.removeEventListener("pointerup", up);
      try { localStorage.setItem(GUIDE_WIDTH_KEY, String(widthRef.current)); } catch { /* preference only */ }
    };
    window.addEventListener("pointermove", move);
    window.addEventListener("pointerup", up);
  };

  return (
    <div ref={panelRef} role="dialog" aria-modal="false" aria-label="Keyboard shortcuts"
      style={{ position: "absolute", top: 0, bottom: 0, left: 0, width, maxWidth: "80%", zIndex: 7, display: "flex", flexDirection: "column", background: "var(--surface)", borderRight: "1px solid var(--cs-border)", boxShadow: "4px 0 16px rgba(0,0,0,0.18)" }}>
      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px 6px" }}>
        <div style={{ fontSize: 15, fontWeight: 700 }}>Keyboard and mouse shortcuts</div>
        <button onClick={onClose} aria-label="Close" style={{ fontSize: 13, border: "1px solid var(--cs-border)", borderRadius: 6, background: "var(--surface)", cursor: "pointer", padding: "2px 10px" }}>✕</button>
      </div>
      <div style={{ flex: 1, overflowY: "auto", padding: "0 14px" }}>
        {CANVAS_SHORTCUT_SECTIONS.map(sec => (
          <section key={sec.title} style={{ marginBottom: 14 }}>
            <h3 style={{ margin: "0 0 4px", fontSize: 11, fontWeight: 700, textTransform: "uppercase", letterSpacing: 0.4, color: "var(--text3)" }}>{sec.title}</h3>
            {sec.items.map((it, i) => (
              <div key={i} style={{ display: "flex", alignItems: "baseline", gap: 10, padding: "3px 0", fontSize: 13 }}>
                <span style={{ width: 120, flexShrink: 0, display: "flex", gap: 4, flexWrap: "wrap" }}>
                  {it.keys.map((k, j) => <kbd key={j} style={cap}>{k}</kbd>)}
                </span>
                <span style={{ color: "var(--text2)" }}>{it.what}</span>
              </div>
            ))}
          </section>
        ))}
      </div>
      <div style={{ fontSize: 11, color: "var(--text3)", borderTop: "1px solid var(--cs-border)", padding: "8px 14px" }}>
        Shortcuts work when the drawing has focus. Ctrl+K finds any other tool or action by name. Drag the right edge to resize.
      </div>
      <div role="separator" aria-orientation="vertical" aria-label="Resize shortcuts panel" onPointerDown={startResize}
        style={{ position: "absolute", top: 0, bottom: 0, right: -3, width: 7, cursor: "col-resize", zIndex: 1 }} />
    </div>
  );
}
